import { ArtifactActionsButtons } from "@/components/artifacts/ArtifactActionsButtons.tsx";
import { ArtifactStatusDate } from "@/components/artifacts/ArtifactStatusDate.tsx";
import { useSecretSubmit } from "@/components/artifacts/view/blocks/secretSubmit.ts";
import { Button } from "@/components/ui/button.tsx";
import { threadQueries, updateThreadToInProgress } from "@/hooks/useThreads.tsx";
import { messageQueries } from "@/shared/messageQueries.ts";
import type { ArtifactAction, HeavyArtifactResponse } from "@/types/artifacts.ts";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useCallback } from "react";

/**
 * The action footer for a heavy artifact: a status-aware date on the left and,
 * when the artifact is waiting on the user, its declarative `actions[]` (e.g.
 * Send / Cancel) on the right. Owns the optimistic flip — firing an action
 * marks the thread in-progress immediately (rolled back by refetch on error) —
 * so the UI responds before the POST round-trips.
 */
export const ArtifactActionsFooter = ({
  artifact,
  threadId,
  onFired,
  onSucceeded,
  onStage,
  stagedAction,
}: {
  artifact: HeavyArtifactResponse;
  threadId: number;
  /** Called synchronously after the optimistic in-progress flip — the caller's
   *  surface-specific close behavior. */
  onFired: () => void;
  /** Called once the action has actually landed. For anything that must not
   *  happen on a failed request — moving a review off the card, say, which
   *  would leave an undelivered draft behind the user. */
  onSucceeded?: () => void;
  /** Under a live review queue: stage the action into the batch instead of
   *  firing it, so the whole review submits as one request. */
  onStage?: (action: ArtifactAction) => void;
  /** The action already staged for this artifact, if any. */
  stagedAction?: ArtifactAction;
}) => {
  const queryClient = useQueryClient();
  const actions = "actions" in artifact && artifact.actions ? artifact.actions : [];
  // A commit the block owns because its payload cannot travel in an action body.
  // Null for every artifact that has no such control, which is all but one.
  const secretSubmit = useSecretSubmit(artifact.id);

  const handleActionFire = useCallback(
    (action: ArtifactAction) => {
      updateThreadToInProgress(queryClient, threadId, action.pendingBody);
      onFired();
    },
    [queryClient, threadId, onFired],
  );

  // Roll back the optimistic `updateThreadToInProgress` by refetching from
  // the server, restoring the real `activityState`, `card.status`, and
  // artifact `actions` so the user can retry.
  const handleActionError = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: threadQueries.all() });
  }, [queryClient]);

  // The optimistic flip patches the *thread* caches, and this footer's own card
  // re-reads the artifact by id — but the thread feed holds its own copy, and
  // that copy is what the omnibox biscuit and the inline message list render.
  // Without this refetch they keep offering Send for an email already on its way
  // (BIG-7171). Matches what the batch-commit mutation does on success.
  const handleActionSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) });
    onSucceeded?.();
  }, [queryClient, threadId, onSucceeded]);

  return (
    <>
      <div className="text-xs">
        <ArtifactStatusDate artifact={artifact} />
      </div>
      {stagedAction ? (
        // Say so where the buttons were. A staged Send has left no trace on the
        // server yet, so a footer that still offered one would read as a click
        // that did nothing — which is exactly how batching got reported as
        // broken from this surface (BIG-7171).
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Check className="size-3.5 shrink-0" />
          {stagedAction.label} queued — sent when you finish the review
        </div>
      ) : (
        (actions.length > 0 || secretSubmit) && (
          <div className="flex items-center gap-2">
            {actions.length > 0 && (
              // Keyed by artifact: the buttons hold "already fired" state, and this
              // footer is a fixed slot that surfaces swap artifacts through (paging a
              // review on the canvas). Without the key the next draft inherits the
              // last one's lock and opens with a dead Send.
              <ArtifactActionsButtons
                key={artifact.id}
                actions={actions}
                layout="flat"
                onActionFire={handleActionFire}
                onActionError={handleActionError}
                onActionSuccess={handleActionSuccess}
                onStage={onStage}
              />
            )}
            {/* A card whose commit cannot be a declarative action, because its
                payload must not travel in one — see `secretSubmit`. Rendered
                after the actions so Cancel sits to its left, which is the order
                the flat layout already puts a discard in. */}
            {secretSubmit && (
              <Button
                size="sm"
                disabled={!secretSubmit.canSubmit || secretSubmit.isPending}
                onClick={secretSubmit.submit}
                data-statsig-name="computer-secret-save"
              >
                {secretSubmit.isPending ? secretSubmit.pendingLabel : secretSubmit.label}
              </Button>
            )}
          </div>
        )
      )}
    </>
  );
};
